import { toast } from '@/components/ui/use-toast';
import { downloadFilefromBuffer } from '@/lib/utils';
import {
  ServiceCreateResponse,
  ServiceCreateUpdateRequest,
  ServiceGetAllRequestBody,
  ServiceGetAllResponse,
  ServiceResponse,
  ServiceSessionResponse,
} from '@/types/service.types';
import { WorkOrderPatchRequest } from '@/types/workOrder.types';
import {
  GenericErrorResponse,
  GenericErrorResponseError,
  WorkOrderResponseSchema,
} from '@brainvaulttech/dgr-react-sdk';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';

export const usePatchWorkOrder = () => {
  return useMutation<
    WorkOrderResponseSchema,
    AxiosError<GenericErrorResponse>,
    { workOrderId: string; data?: WorkOrderPatchRequest }
  >({
    mutationFn: async ({ workOrderId, data }: { workOrderId: string; data?: WorkOrderPatchRequest }) => {
      const response = await axios.patch<WorkOrderResponseSchema>(`/work-orders/${workOrderId}`, data);
      return response.data;
    },
  });
};

export const getAllServices = async (
  requestBody: ServiceGetAllRequestBody
): Promise<{
  totalRows: number;
  services: ServiceResponse[];
}> => {
  const response = await axios.post<ServiceGetAllResponse>(`/services/getAll`, requestBody);

  return {
    totalRows: response.data.totalRows,
    services: response.data.services,
  };
};

export const downloadWorkOrderPDF = async (
  workOrderId: string,
  workOrderNumber?: string
): Promise<boolean> => {
  toast({
    description: 'Download will start shortly',
    duration: 3000,
  });
  try {
    const response = await axios.get<Blob>(`/work-orders/${workOrderId}/pdf`, {
      responseType: 'blob',
    });
    downloadFilefromBuffer(response.data, `${workOrderNumber}.pdf`, 'PDF');
    return true;
  } catch (err) {
    toast({
      description: 'Unable to generate PDF, please try again',
      duration: 3000,
      variant: 'destructive',
    });
    return false;
  }
};

export const useCreateService = () => {
  return useMutation<ServiceResponse, GenericErrorResponse, { data: ServiceCreateUpdateRequest }>({
    mutationFn: async ({ data }) => {
      const response = await axios.post<ServiceResponse>(`/services`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const useUpdateServiceById = () => {
  return useMutation<ServiceResponse, GenericErrorResponse, { serviceId?: string; data: ServiceCreateUpdateRequest }>({
    mutationFn: async ({ serviceId, data }) => {
      const response = await axios.put<ServiceResponse>(`/services/${serviceId}`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const useGetServiceById = (serviceId: string | undefined) => {
  return useQuery<AxiosResponse<ServiceResponse>, GenericErrorResponseError>({
    queryKey: ['service', serviceId],
    queryFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.get<ServiceResponse>(`/services/${serviceId}`);
      return response;
    },
    enabled: !!serviceId,
  });
};

export const useGetServiceSession = (serviceId: string | undefined) => {
  return useQuery<AxiosResponse<ServiceSessionResponse>, GenericErrorResponseError>({
    queryKey: ['service-session', serviceId],
    queryFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.get<ServiceSessionResponse>(`/services/${serviceId}/session`);
      return response;
    },
    enabled: !!serviceId,
  });
};

export const useCreateServiceSession = (serviceId: string | undefined) => {
  return useMutation<ServiceCreateResponse, GenericErrorResponse>({
    mutationFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.post<ServiceCreateResponse>(`/services/${serviceId}/session`);
      return response.data;
    },
  });
};

export const useRefreshServiceSession = (serviceId: string | undefined) => {
  return useMutation<ServiceCreateResponse, GenericErrorResponse>({
    mutationFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.patch<ServiceCreateResponse>(`/services/${serviceId}/session/refresh`);
      return response.data;
    },
  });
};

export const useEndServiceSession = (serviceId: string | undefined) => {
  return useMutation<ServiceCreateResponse, GenericErrorResponse>({
    mutationFn: async () => {
      if (!serviceId) throw new Error('Service ID is required');
      const response = await axios.patch<ServiceCreateResponse>(`/services/${serviceId}/session/end`);
      return response.data;
    },
  });
};
